import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Wrench, Clock, ClipboardList, User, ChevronDown, X } from 'lucide-react';
import { getFicheParToken, validerFiche } from '../api';

const fmtDate = (d) => {
  try {
    const dt = typeof d === 'string' ? new Date(d) : d;
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return d || '—'; }
};
const fmtH = (h) => h != null ? `${Number(h).toFixed(2)} h` : '—';

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      <span style={{ color: '#94A3B8', fontSize: '0.7rem', display: 'block' }}>{label}</span>
      <span style={{ color: '#1E293B', fontSize: '0.875rem', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function Section({ icon: Icon, title, color, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Icon size={14} style={{ color }} />
        <span style={{ color, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

/* ── Sélecteur signataire — pill + dropdown mono-sélection ─────────────────── */
function SignataireSelector({ contacts, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualVal, setManualVal] = useState('');
  const ref = useRef(null);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Si pas de contacts disponibles → input manuel classique
  if (!contacts || contacts.length === 0) {
    return (
      <input
        type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder="Nom et prénom"
        data-testid="signataire-nom-input"
        style={{
          width: '100%', boxSizing: 'border-box',
          border: '1px solid #DDD6FE', borderRadius: 10,
          padding: '10px 14px', fontSize: '0.875rem', color: '#1E293B',
          outline: 'none', background: '#FAF5FF',
        }}
      />
    );
  }

  const selectedContact = contacts.find(c => c.nom === value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Zone pill / placeholder */}
      <div
        onClick={() => { setOpen(o => !o); setManualMode(false); }}
        data-testid="signataire-selector-trigger"
        style={{
          minHeight: 44, border: '1px solid #DDD6FE', borderRadius: 10,
          padding: '8px 36px 8px 12px', background: '#FAF5FF', cursor: 'pointer',
          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
          position: 'relative',
        }}
      >
        {value ? (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#EDE9FE', color: '#5B21B6', borderRadius: 20,
              padding: '3px 10px 3px 10px', fontSize: '0.82rem', fontWeight: 600,
            }}
          >
            <User size={12} />
            {value}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onChange(''); }}
              data-testid="signataire-pill-remove"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 2, display: 'flex', alignItems: 'center' }}
            >
              <X size={12} color="#7C3AED" />
            </button>
          </span>
        ) : (
          <span style={{ color: '#94A3B8', fontSize: '0.875rem' }}>Sélectionner un signataire…</span>
        )}
        <ChevronDown size={16} style={{ position: 'absolute', right: 12, top: '50%', transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)', color: '#94A3B8', transition: 'transform 0.15s' }} />
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', borderRadius: 12, border: '1px solid #DDD6FE',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4, overflow: 'hidden',
        }}>
          {contacts.map(c => {
            const selected = value === c.nom;
            return (
              <div
                key={c.personne_id}
                onClick={() => { onChange(selected ? '' : c.nom); setOpen(false); }}
                data-testid={`signataire-option-${c.personne_id}`}
                style={{
                  padding: '10px 14px', cursor: 'pointer',
                  background: selected ? '#F5F3FF' : '#fff',
                  display: 'flex', alignItems: 'center', gap: 10,
                  borderBottom: '1px solid #F1F5F9',
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${selected ? '#7C3AED' : '#D1D5DB'}`,
                  background: selected ? '#7C3AED' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                </div>
                <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#1E293B' }}>{c.nom}</p>
              </div>
            );
          })}

          {/* Saisie libre */}
          <div
            style={{ padding: '8px 14px', borderTop: '1px solid #E2E8F0' }}
            onClick={e => e.stopPropagation()}
          >
            {manualMode ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  autoFocus
                  type="text"
                  value={manualVal}
                  onChange={e => setManualVal(e.target.value)}
                  placeholder="Autre nom…"
                  style={{
                    flex: 1, border: '1px solid #DDD6FE', borderRadius: 8,
                    padding: '7px 10px', fontSize: '0.82rem', outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={() => { if (manualVal.trim()) { onChange(manualVal.trim()); setManualVal(''); } setManualMode(false); setOpen(false); }}
                  style={{
                    padding: '7px 12px', borderRadius: 8, border: 'none',
                    background: '#7C3AED', color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                  }}
                >
                  OK
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setManualMode(true)}
                style={{ background: 'none', border: 'none', color: '#7C3AED', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '2px 0' }}
              >
                + Saisir un autre nom
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Page principale ────────────────────────────────────────────────────────── */
export default function AtelierFicheInterventionPage() {
  const { token } = useParams();
  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef(null);

  const [fiche,      setFiche]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [nom,        setNom]        = useState('');
  const [avecSig,    setAvecSig]    = useState(true);
  const [submitted,  setSubmitted]  = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getFicheParToken(token)
      .then(r => {
        setFiche(r.data);
        // Pré-remplir avec le premier contact
        const contacts = r.data?.contacts_list || [];
        if (contacts.length > 0) setNom(contacts[0].nom);
      })
      .catch(e => setError(e?.response?.data?.detail || 'Fiche invalide ou expirée'))
      .finally(() => setLoading(false));
  }, [token]);

  /* ── Canvas ──────────────────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !fiche || !avecSig) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#7C3AED'; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches?.[0] || e;
      return { x: (t.clientX - r.left) * (canvas.width / r.width),
               y: (t.clientY - r.top)  * (canvas.height / r.height) };
    };
    const down = (e) => { e.preventDefault(); drawingRef.current = true; lastPosRef.current = pos(e); };
    const move = (e) => {
      e.preventDefault();
      if (!drawingRef.current) return;
      const p = pos(e);
      ctx.beginPath(); ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
      lastPosRef.current = p;
    };
    const up = () => { drawingRef.current = false; };
    canvas.addEventListener('mousedown',  down);
    canvas.addEventListener('mousemove',  move);
    canvas.addEventListener('mouseup',    up);
    canvas.addEventListener('touchstart', down, { passive: false });
    canvas.addEventListener('touchmove',  move, { passive: false });
    canvas.addEventListener('touchend',   up);
    return () => {
      canvas.removeEventListener('mousedown',  down);
      canvas.removeEventListener('mousemove',  move);
      canvas.removeEventListener('mouseup',    up);
      canvas.removeEventListener('touchstart', down);
      canvas.removeEventListener('touchmove',  move);
      canvas.removeEventListener('touchend',   up);
    };
  }, [fiche, avecSig]);

  const clearCanvas = () => { const c = canvasRef.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); };
  const isCanvasEmpty = () => {
    const c = canvasRef.current; if (!c) return true;
    const b = document.createElement('canvas'); b.width = c.width; b.height = c.height;
    return c.toDataURL() === b.toDataURL();
  };

  const handleSubmit = async () => {
    if (avecSig && !nom.trim())     { alert('Merci de saisir le nom du signataire.'); return; }
    if (avecSig && isCanvasEmpty()) { alert("Merci d'apposer votre signature."); return; }
    setSubmitting(true);
    // Récupérer l'email du signataire depuis la contacts_list
    const contacts = fiche?.contacts_list || [];
    const sigContact = contacts.find(c => c.nom === nom.trim());
    const sigEmail = sigContact?.email || '';
    try {
      await validerFiche(token, {
        signataire_nom:      avecSig ? nom.trim() : null,
        signataire_email:    avecSig ? sigEmail   : null,
        signature_image_b64: avecSig ? canvasRef.current?.toDataURL('image/png') : null,
      });
      setSubmitted(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── États ──────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F3FF' }}>
      <Loader2 size={32} style={{ color: '#7C3AED', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FEF2F2', padding: 24 }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: '#DC2626', fontWeight: 700, marginBottom: 8 }}>Fiche invalide</p>
        <p style={{ color: '#EF4444', fontSize: '0.875rem' }}>{error}</p>
      </div>
    </div>
  );

  if (fiche?.statut === 'validee' || submitted) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F0FDF4', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 380 }}>
        <CheckCircle2 size={56} style={{ color: '#16A34A', margin: '0 auto 16px', display: 'block' }} />
        <p style={{ color: '#15803D', fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Fiche validée</p>
        <p style={{ color: '#166534', fontSize: '0.875rem' }}>
          La fiche d'intervention <strong>{fiche?.numero}</strong> a été enregistrée.
          {nom && <span> Merci <strong>{nom}</strong>.</span>}
        </p>
      </div>
    </div>
  );

  const pointages   = Array.isArray(fiche?.pointages)     ? fiche.pointages     : [];
  const contacts    = Array.isArray(fiche?.contacts_list) ? fiche.contacts_list : [];
  const travaux     = (fiche?.travaux_effectues || '').trim();

  return (
    <div style={{ minHeight: '100vh', background: '#F5F3FF',  }}>

      {/* Header */}
      <div style={{ background: '#7C3AED', padding: '16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Wrench size={20} color="#fff" />
          <div>
            <p style={{ color: '#fff', fontWeight: 800, fontSize: '1rem', margin: 0 }}>Bedee — Atelier</p>
            <p style={{ color: '#DDD6FE', fontSize: '0.72rem', margin: '2px 0 0' }}>Fiche d'intervention</p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '24px auto', padding: '0 16px 48px' }}>

        {/* En-tête fiche */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0', padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <p style={{ color: '#94A3B8', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>
                Fiche d'intervention
              </p>
              <p style={{ color: '#7C3AED', fontWeight: 800, fontSize: '1.1rem', margin: 0 }}>{fiche.numero}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ color: '#94A3B8', fontSize: '0.7rem', margin: '0 0 2px' }}>Date</p>
              <p style={{ color: '#1E293B', fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>{fmtDate(fiche.date_intervention)}</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <InfoRow label="Client"          value={fiche.client_nom} />
            <InfoRow label="N° OR"           value={fiche.or_numero} />
            <InfoRow label="Matériel"        value={fiche.materiel_nom} />
            <InfoRow label="Marque"          value={fiche.materiel_marque} />
            <InfoRow label="N° Série"        value={fiche.numero_serie} />
            <InfoRow label="Modèle"          value={fiche.materiel_modele} />
            <InfoRow label="Immatriculation" value={fiche.immatriculation} />
            <InfoRow label="N° Parc"         value={fiche.numero_parc} />
            <InfoRow label="Objet"           value={fiche.or_titre} />
            {fiche.lieu_intervention && <InfoRow label="Lieu" value={fiche.lieu_intervention} />}
          </div>
        </div>

        {/* Pointages */}
        {pointages.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0', padding: 20, marginBottom: 16 }}>
            <Section icon={Clock} title="Pointages du jour" color="#D97706">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pointages.map((pt, i) => (
                  <div key={i} style={{ background: '#FFFBEB', borderRadius: 10, padding: '10px 14px', border: '1px solid #FDE68A' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pt.note ? 6 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <User size={13} style={{ color: '#D97706' }} />
                        <strong style={{ color: '#92400E', fontSize: '0.875rem' }}>{pt.employe_nom || '—'}</strong>
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', color: '#B45309' }}>
                        {pt.heure_arrivee && <span>Arrivée : {(pt.heure_arrivee||'').slice(0,5)}</span>}
                        {pt.heure_depart  && <span>Départ : {(pt.heure_depart||'').slice(0,5)}</span>}
                        {pt.duree_reelle != null && <span style={{ fontWeight: 700 }}>{fmtH(pt.duree_reelle)}</span>}
                      </div>
                    </div>
                    {pt.note && (
                      <p style={{ color: '#78350F', fontSize: '0.8rem', margin: '4px 0 0', lineHeight: 1.5 }}>{pt.note}</p>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          </div>
        )}

        {/* Travaux effectués */}
        {travaux && (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0', padding: 20, marginBottom: 16 }}>
            <Section icon={ClipboardList} title="Travaux effectués" color="#059669">
              <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '10px 14px', border: '1px solid #BBF7D0' }}>
                {travaux.split('\n').map((line, i) => {
                  const bold = line.match(/^\*\*(.+)\*\*$/);
                  return (
                    <React.Fragment key={i}>
                      {bold
                        ? <strong style={{ color: '#065F46', display: 'block', marginTop: i > 0 ? 8 : 0 }}>{bold[1]}</strong>
                        : <span style={{ color: '#134E4A', fontSize: '0.85rem', display: 'block' }}>{line || '\u00A0'}</span>
                      }
                    </React.Fragment>
                  );
                })}
              </div>
            </Section>
          </div>
        )}

        {/* Signature */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0', padding: 20 }}>
          <p style={{ color: '#94A3B8', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>
            Validation
          </p>

          {/* Toggle signature */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[true, false].map(v => (
              <button key={String(v)}
                onClick={() => setAvecSig(v)}
                style={{
                  flex: 1, padding: '10px 8px', borderRadius: 10, fontSize: '0.82rem', fontWeight: 600,
                  border: `2px solid ${avecSig === v ? '#7C3AED' : '#E2E8F0'}`,
                  background: avecSig === v ? '#FAF5FF' : '#fff',
                  color: avecSig === v ? '#7C3AED' : '#94A3B8',
                  cursor: 'pointer',
                }}>
                {v ? 'Signer' : 'Valider sans signature'}
              </button>
            ))}
          </div>

          {avecSig && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', color: '#475569', fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>
                  Nom du signataire <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <SignataireSelector
                  contacts={contacts}
                  value={nom}
                  onChange={setNom}
                />
              </div>

              <div style={{ position: 'relative', touchAction: 'none' }}>
                <canvas
                  ref={canvasRef} width={600} height={320}
                  data-testid="signature-canvas"
                  style={{ display: 'block', width: '100%', cursor: 'crosshair',
                           background: '#FAFAFA', borderRadius: 12, border: '2px dashed #DDD6FE' }}
                />
                <p style={{ position: 'absolute', top: '50%', left: '50%',
                            transform: 'translate(-50%,-50%)',
                            color: '#DDD6FE', fontSize: '0.85rem',
                            pointerEvents: 'none', margin: 0, whiteSpace: 'nowrap', userSelect: 'none' }}>
                  Signez ici
                </p>
              </div>

              <button onClick={clearCanvas}
                style={{ marginTop: 8, padding: '8px 14px', borderRadius: 10,
                         border: '1px solid #DDD6FE', background: '#FAF5FF',
                         color: '#7C3AED', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                Effacer
              </button>
            </>
          )}

          <button
            onClick={handleSubmit} disabled={submitting}
            data-testid="btn-valider-fiche"
            style={{
              width: '100%', marginTop: 16, padding: 13, borderRadius: 12, border: 'none',
              background: submitting ? '#EDE9FE' : '#7C3AED',
              color: submitting ? '#9C81E8' : '#fff',
              fontSize: '0.9rem', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
            {submitting
              ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Génération du PDF…</>
              : <><CheckCircle2 size={14} /> {avecSig ? 'Signer et valider' : 'Valider la fiche'}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
